export const taskStructureTemplates = {
  basic: {
    name: "Basic Task",
    description: "Simple task structure with basic fields",
    definition: {
      fields: [],
      workflowSteps: ["Not Started", "In Progress", "Completed"],
      roles: ["Assignee", "Observer"]
    }
  },
  project: {
    name: "Project Task",
    description: "Complex project task with milestones and team assignments",
    definition: {
      fields: [
        {
          name: "budget",
          type: "number",
          label: "Budget",
          required: false
        },
        {
          name: "teamMembers",
          type: "multi-user",
          label: "Team Members",
          required: true
        },
        {
          name: "involvedDepartments",
          type: "multi-organization",
          label: "Involved Departments",
          required: true
        },
        {
          name: "milestones",
          type: "checklist",
          label: "Project Milestones",
          required: true,
          items: [
            "Project kickoff",
            "Requirements gathering",
            "Design phase complete",
            "Development complete",
            "Testing complete",
            "Final review",
            "Deployment"
          ]
        },
        {
          name: "deliverables",
          type: "checklist",
          label: "Project Deliverables",
          required: true,
          items: [
            "Project plan",
            "Requirements document",
            "Design specifications",
            "Progress reports",
            "Test results",
            "Final documentation",
            "Training materials"
          ]
        }
      ],
      workflowSteps: ["Planning", "In Progress", "Review", "Testing", "Completed"],
      roles: ["Project Manager", "Team Member", "Stakeholder", "Observer"]
    }
  },
  checklist: {
    name: "Checklist Task",
    description: "Task with checklist items and verification steps",
    definition: {
      fields: [
        {
          name: "verifiers",
          type: "multi-user",
          label: "Verifiers",
          required: true
        },
        {
          name: "responsibleDepartments",
          type: "multi-organization",
          label: "Responsible Departments",
          required: true
        },
        {
          name: "checkItems",
          type: "checklist",
          label: "Inspection Items",
          required: true,
          items: [
            "Check equipment condition",
            "Verify safety protocols",
            "Test functionality",
            "Document findings"
          ]
        },
        {
          name: "safetyChecks",
          type: "checklist",
          label: "Safety Requirements",
          required: true,
          items: [
            "PPE requirements met",
            "Area secured",
            "Safety briefing completed",
            "Emergency procedures reviewed"
          ]
        }
      ],
      workflowSteps: ["Open", "In Progress", "Inspection", "Verification", "Completed"],
      roles: ["Inspector", "Verifier", "Assignee"]
    }
  },
  approval: {
    name: "Approval Task",
    description: "Task requiring multiple levels of approval",
    definition: {
      fields: [
        {
          name: "approvers",
          type: "multi-user",
          label: "Approvers",
          required: true
        },
        {
          name: "involvedDepartments",
          type: "multi-organization",
          label: "Involved Departments",
          required: true
        },
        {
          name: "approvalSteps",
          type: "checklist",
          label: "Approval Steps",
          required: true
        },
        {
          name: "requirements",
          type: "checklist",
          label: "Requirements Checklist",
          required: true
        }
      ],
      workflowSteps: ["Draft", "Submitted", "Under Review", "Approved", "Rejected"],
      roles: ["Requestor", "Approver", "Admin"]
    }
  },
  maintenance: {
    name: "Maintenance Task",
    description: "Task for equipment or system maintenance with detailed tracking",
    definition: {
      fields: [
        {
          name: "assetId",
          type: "text",
          label: "Asset/Equipment ID",
          required: true
        },
        {
          name: "maintenanceType",
          type: "select",
          label: "Maintenance Type",
          required: true,
          options: ["Preventive", "Corrective", "Predictive", "Emergency"]
        },
        {
          name: "technicians",
          type: "multi-user",
          label: "Assigned Technicians",
          required: true
        },
        {
          name: "departments",
          type: "multi-organization",
          label: "Responsible Departments",
          required: true
        },
        {
          name: "maintenanceChecklist",
          type: "checklist",
          label: "Maintenance Checklist",
          required: true,
          items: [
            "Initial inspection",
            "Clean equipment",
            "Replace worn parts",
            "Lubricate components",
            "Test operation",
            "Final inspection"
          ]
        },
        {
          name: "safetyChecklist",
          type: "checklist",
          label: "Safety Requirements",
          required: true,
          items: [
            "Lock out/Tag out",
            "Work area secured",
            "PPE in place",
            "Tools inspected"
          ]
        },
        {
          name: "partsChecklist",
          type: "checklist",
          label: "Required Parts",
          required: true,
          items: [
            "Replacement parts available",
            "Tools available",
            "Special equipment ready",
            "Consumables in stock"
          ]
        }
      ],
      workflowSteps: ["Scheduled", "Parts Ordered", "In Progress", "Testing", "Completed"],
      roles: ["Technician", "Supervisor", "Quality Inspector"]
    }
  },
  incident: {
    name: "Incident Task",
    description: "Task for tracking and resolving incidents or issues",
    definition: {
      fields: [
        {
          name: "severity",
          type: "select",
          label: "Severity Level",
          required: true,
          options: ["Critical", "High", "Medium", "Low"]
        },
        {
          name: "category",
          type: "select",
          label: "Incident Category",
          required: true,
          options: ["System", "Security", "Hardware", "Software", "Network", "User Access"]
        },
        {
          name: "assignedTeam",
          type: "multi-user",
          label: "Assigned Team",
          required: true
        },
        {
          name: "affectedDepartments",
          type: "multi-organization",
          label: "Affected Departments",
          required: true
        },
        {
          name: "investigationSteps",
          type: "checklist",
          label: "Investigation Steps",
          required: true
        },
        {
          name: "resolutionSteps",
          type: "checklist",
          label: "Resolution Steps",
          required: false
        },
        {
          name: "preventiveMeasures",
          type: "checklist",
          label: "Preventive Measures",
          required: false
        }
      ],
      workflowSteps: ["Reported", "Investigating", "In Progress", "Testing", "Resolved", "Closed"],
      roles: ["Reporter", "Investigator", "Resolver", "Validator"]
    }
  },
  review: {
    name: "Review Task",
    description: "Task for document or code review processes",
    definition: {
      fields: [
        {
          name: "itemType",
          type: "select",
          label: "Item Type",
          required: true,
          options: ["Document", "Code", "Design", "Process"]
        },
        {
          name: "reviewers",
          type: "multi-user",
          label: "Reviewers",
          required: true
        },
        {
          name: "stakeholders",
          type: "multi-user",
          label: "Stakeholders",
          required: false
        },
        {
          name: "departments",
          type: "multi-organization",
          label: "Involved Departments",
          required: true
        },
        {
          name: "reviewChecklist",
          type: "checklist",
          label: "Review Checklist",
          required: true
        },
        {
          name: "feedbackItems",
          type: "checklist",
          label: "Feedback Items",
          required: false
        }
      ],
      workflowSteps: ["Draft", "Under Review", "Revisions Required", "Approved", "Completed"],
      roles: ["Author", "Reviewer", "Approver"]
    }
  },
  privateChat: {
    name: "Private Chat",
    description: "Task for private communication between team members",
    definition: {
      fields: [
        {
          name: "participants",
          type: "multi-user",
          label: "Chat Participants",
          required: true
        },
        {
          name: "isEncrypted",
          type: "boolean",
          label: "Enable End-to-End Encryption",
          required: false
        },
        {
          name: "retentionPeriod",
          type: "select",
          label: "Message Retention Period",
          required: false,
          options: ["24 Hours", "7 Days", "30 Days", "90 Days", "Forever"]
        },
        {
          name: "attachmentsAllowed",
          type: "boolean",
          label: "Allow Attachments",
          required: false
        }
      ],
      workflowSteps: ["Active", "Archived"],
      roles: ["Participant", "Admin"]
    }
  }
};